/**
 * The last thing between a render error and a white page (PLAN-LAUNCH E3).
 *
 * React unmounts the whole tree when a render, lifecycle or effect throws and
 * nothing catches it — the user gets a blank document with no explanation and
 * no way back. This is the one boundary that stops that, mounted outside every
 * provider in `main.tsx`.
 *
 * Two jobs, and the second is the reason it exists at all:
 *
 * 1. render something honest — the same full-page affordance a missing recipe
 *    or a mistyped URL gets ({@link NotFoundPage}), because a crash and a dead
 *    end should feel like the same product rather than like two different bugs;
 * 2. **report**. A render error never reaches `window.onerror`: React catches
 *    it and hands it to `componentDidCatch`, so an SDK's global handlers cannot
 *    see it. Without this component the entire class of errors that actually
 *    break the UI would be invisible in the dashboard.
 *
 * A class component because there is still no hook for this: `componentDidCatch`
 * and `getDerivedStateFromError` are the only React API that catches a render
 * throw, and both are class-only.
 *
 * ## Why "Reload" and not "Try again"
 *
 * A retry that re-renders the same tree with the same state re-throws
 * immediately, which reads to the user as a button that does nothing. A reload
 * is the honest offer: the workspace is in IndexedDB and survives it, so the
 * cost of taking it is a second of loading and never a lost recipe.
 */

import type { ErrorInfo, ReactNode } from 'react';
import { Component } from 'react';
import { NotFoundPage } from '@/components/not-found-page';
import { Button } from '@/components/ui/button';
import { reportError } from '@/lib/posthog';

export interface ErrorBoundaryProps {
  readonly children: ReactNode;
  /**
   * Where a caught error goes. Defaults to PostHog's reporter, which is a no-op
   * on a deployment with no key.
   *
   * Injectable so the boundary's own test can assert *that* it reports without
   * loading an SDK, and so a future caller can add a second sink without this
   * file learning about it.
   */
  readonly onError?: (error: unknown, info: ErrorInfo) => void;
}

interface ErrorBoundaryState {
  readonly crashed: boolean;
}

export class ErrorBoundary extends Component<ErrorBoundaryProps, ErrorBoundaryState> {
  override state: ErrorBoundaryState = { crashed: false };

  /** Switch to the fallback. Separate from reporting, which React runs after. */
  static getDerivedStateFromError(): ErrorBoundaryState {
    return { crashed: true };
  }

  override componentDidCatch(error: unknown, info: ErrorInfo): void {
    // The console line is not redundant with the report: a developer with no
    // PostHog key configured — which is every development build — would
    // otherwise get a fallback page and no stack anywhere.
    console.error('[beerwright] unhandled render error', error);
    (this.props.onError ?? reportError)(error, info);
  }

  override render(): ReactNode {
    if (!this.state.crashed) return this.props.children;
    return <CrashPage />;
  }
}

/**
 * The page every unrecoverable error renders, wherever it was caught.
 *
 * Shared rather than written twice because there are two catchers and they must
 * not drift: this boundary, which sits outside every provider, and
 * `RouteError`, which the router reaches first for anything thrown inside a
 * route. A user should not be able to tell which one caught their crash.
 *
 * The description leads with the thing a brewer actually fears. "Something went
 * wrong" says nothing about whether the recipe they spent an hour on is gone,
 * and it is not: the workspace is in IndexedDB and outlives any render.
 */
export function CrashPage({ action }: { readonly action?: ReactNode }) {
  return (
    <NotFoundPage
      title="Something went wrong"
      description="Beerwright hit an error it could not recover from. Your recipes are stored on this device and are safe, so reloading should bring everything back."
      action={
        action ?? (
          <Button type="button" onClick={() => window.location.reload()}>
            Reload Beerwright
          </Button>
        )
      }
    />
  );
}
